import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Alert, AlertTitle } from '@mui/material';
import Login from './components/login/Login';
import ResetPassword from './components/login/ResetPassword';
import Home from './components/home/Home';
// import ProtectedRoute from './components/common/ProtectedRoute'; // No usado actualmente
import MainLayout from './components/layout/MainLayout';
import NetworkErrorNotifier from './components/common/NetworkErrorNotifier';
import AppUpdateNotifier from './components/common/AppUpdateNotifier';
import CatalogoServicios from './components/servicios/CatalogoServicios';
import AgendaServicios from './components/servicios/AgendaServicios';
import OrdenesTrabajoList from './components/servicios/OrdenesTrabajoList';
import Programas from './components/servicios/Programas';
import Capacitaciones from './components/servicios/Capacitaciones';
import RevisionCalidad from './components/servicios/RevisionCalidad';
import CentroGestionDocumental from './components/servicios/CentroGestionDocumental';
import NewQuotation from './components/servicios/NewQuotation';
import CatalogoUsuarios from './components/usuarios/CatalogoUsuarios';
import PerfilesUsuarios from './components/usuarios/PerfilesUsuarios';
import AnalyticaUsuarios from './components/usuarios/AnalyticaUsuarios';
import GestionRoles from './components/usuarios/GestionRoles';
import Profile from './components/profile/Profile';
import EstadisticasCotizaciones from './components/estadisticas/EstadisticasCotizaciones';
import FinanzasAbonos from './components/finanzas/FinanzasAbonos';
import ConfiguracionCorreos from './components/utilerias/ConfiguracionCorreos';
import ConfiguracionDropbox from './components/utilerias/ConfiguracionDropbox';
import ConfiguracionSustanciasCosto from './components/utilerias/ConfiguracionSustanciasCosto';
import DropboxCallback from './components/dropbox/DropboxCallback';
import NuestraEmpresa from './components/empresa/NuestraEmpresa';
import MisionVision from './components/empresa/MisionVision';
import NuestroEquipo from './components/empresa/NuestroEquipo';
import Historia from './components/empresa/Historia';
import Contacto from './components/empresa/Contacto';
import CatalogoEmpresas from './components/clientes/CatalogoEmpresas';
import GestionSucursales from './components/clientes/GestionSucursales';
import LlenarInformacion from './components/perfil-riesgo/LlenarInformacion';
import SubirArchivos from './components/perfil-riesgo/SubirArchivos';
import Historial from './components/perfil-riesgo/Historial';
import MisDocumentosEntregados from './components/perfil-riesgo/MisDocumentosEntregados';
import EvaluacionInspeccionCompleta from './components/evaluaciones/EvaluacionInspeccionCompleta';
import { OFFLINE_FIELD_MODE } from './config/features';
import TablaSeguimiento from './components/admin/TablaSeguimiento';
import ConfirmarCotizacion from './components/cotizaciones/ConfirmarCotizacion';
import PortalInvitado from './components/portal/PortalInvitado';
import permissionService from './services/permissionService';
import './App.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = React.useState(() => {
    return sessionStorage.getItem('authToken') !== null;
  });

  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userInfo');
    permissionService.clearCache();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    console.log('✅ Sesión cerrada - Tokens eliminados');
    setIsLoggedIn(false);
  };

  const PrivateRoute = ({ children }) => {
    return isLoggedIn ? children : <Navigate to="/login" replace />;
  };

  const PublicRoute = ({ children }) => {
    return !isLoggedIn ? children : <Navigate to="/" replace />;
  };

  // Guard por rol para Revisión de Calidad: role_screens en BD ya restringe esta pantalla a
  // ADMINISTRADOR/SUPERVISOR_CALIDAD, pero eso hoy solo se usa para ocultar el ítem del menú
  // (Navbar.js) -- la ruta en sí no estaba protegida, así que cualquier autenticado podía entrar
  // por URL directa o por el chip de estado QA en Gestión Documental. En vez de redirigir
  // (confuso para quien ya está logueado), se muestra en el lugar un aviso explícito de por qué
  // no puede entrar -- mismo criterio de "hacerlo evidente" que pidió Omar para el chip.
  const QA_ALLOWED_ROLES = ['ADMINISTRADOR', 'SUPERVISOR_CALIDAD'];
  const getUserRoleName = () => {
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const role = userInfo?.role;
      return typeof role === 'string' ? role : role?.name;
    } catch {
      return null;
    }
  };

  const QaRoute = ({ children }) => {
    const roleName = getUserRoleName();
    if (QA_ALLOWED_ROLES.includes((roleName || '').toUpperCase())) {
      return children;
    }
    return (
      <MainLayout onLogout={handleLogout}>
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <Alert severity="warning" sx={{ maxWidth: 520 }}>
            <AlertTitle>Acceso restringido</AlertTitle>
            Solo Administradores y Supervisores de Calidad pueden acceder a Revisión de Calidad.
          </Alert>
        </Box>
      </MainLayout>
    );
  };

  return (
    <Router>
      <NetworkErrorNotifier />
      <AppUpdateNotifier />
      <Routes>
        <Route path="/" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Home />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/home" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Home />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/catalogo" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <CatalogoServicios />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/agenda" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <AgendaServicios />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/ordenes-trabajo" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <OrdenesTrabajoList />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/programas" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Programas />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/documentos" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <CentroGestionDocumental />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/capacitaciones" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Capacitaciones />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/servicios/revision-calidad" element={
          <PrivateRoute>
            <QaRoute>
              <MainLayout onLogout={handleLogout}>
                <RevisionCalidad />
              </MainLayout>
            </QaRoute>
          </PrivateRoute>
        } />
        <Route path="/servicios" element={<Navigate to="/servicios/catalogo" replace />} />
        <Route path="/confirmar-cotizacion/:folio" element={<ConfirmarCotizacion />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/activar-cuenta/:token" element={<ResetPassword mode="activation" />} />
        <Route path="/login" element={
          <PublicRoute>
            <Login
              onLogin={() => setIsLoggedIn(true)}
            />
          </PublicRoute>
        } />
        <Route path="/portal" element={
          <PrivateRoute>
            <PortalInvitado />
          </PrivateRoute>
        } />
        <Route path="/cotizaciones/new-quotation" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <NewQuotation />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/usuarios/catalogo" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <CatalogoUsuarios />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/usuarios/perfiles" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <PerfilesUsuarios />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/usuarios/analitica" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <AnalyticaUsuarios />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/usuarios/roles" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <GestionRoles />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/usuarios" element={<Navigate to="/usuarios/catalogo" replace />} />
        <Route path="/profile" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Profile />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/estadisticas/cotizaciones" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <EstadisticasCotizaciones />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/estadisticas" element={<Navigate to="/estadisticas/cotizaciones" replace />} />
        <Route path="/finanzas/abonos" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <FinanzasAbonos />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/utilerias/correos" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <ConfiguracionCorreos />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/utilerias/dropbox" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <ConfiguracionDropbox />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/utilerias/sustancias-costo" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <ConfiguracionSustanciasCosto />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/utilerias" element={<Navigate to="/utilerias/correos" replace />} />
        <Route path="/dropbox/callback" element={<DropboxCallback />} />
        <Route path="/nosotros/empresa" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <NuestraEmpresa />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/nosotros/mision-vision" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <MisionVision />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/nosotros/equipo" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <NuestroEquipo />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/nosotros/historia" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Historia />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/nosotros/contacto" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Contacto />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/clientes/empresas" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <CatalogoEmpresas />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/clientes/sucursales" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <GestionSucursales />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/clientes" element={<Navigate to="/clientes/empresas" replace />} />
        <Route path="/perfil-riesgo/llenar-informacion" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <LlenarInformacion />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/perfil-riesgo/subir-archivos" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <SubirArchivos />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/perfil-riesgo/historial" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <Historial />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/perfil-riesgo/mis-documentos" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <MisDocumentosEntregados />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/perfil-riesgo" element={<Navigate to="/perfil-riesgo/llenar-informacion" replace />} />
        <Route path="/tablero-seguimiento" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <TablaSeguimiento />
            </MainLayout>
          </PrivateRoute>
        } />
        <Route path="/evaluaciones/inspeccion" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <EvaluacionInspeccionCompleta />
            </MainLayout>
          </PrivateRoute>
        } />
        {OFFLINE_FIELD_MODE && <Route path="/evaluaciones/asincrona/:asyncId" element={
          <PrivateRoute>
            <MainLayout onLogout={handleLogout}>
              <EvaluacionInspeccionCompleta key="async" />
            </MainLayout>
          </PrivateRoute>
        } />}
      </Routes>
    </Router>
  );
}

export default App;
